import { useAPI } from '@providers/useAPI';
import { useQuery } from '@tanstack/react-query';
import { GetJobUtterancesResponseDto } from 'app/API';
import { adminJobsKeys } from 'app/queries/adminJobs/adminJobsKeys';
import { createFetchJobUtterancesQueryFn } from 'app/queries/adminJobs/createFetchJobUtterancesQueryFn';
import { createContext, ReactNode, useContext, useState } from 'react';
import { useParams } from 'react-router-dom';

const ASSEMBLY_TRANSCRIPT = 'asr.json';
const EMPTY_UTTERANCES: GetJobUtterancesResponseDto['utterances'] = [];

type TState = {
  jobId: string;
  isLoading: boolean;
  error: string | null;
  utterances: GetJobUtterancesResponseDto['utterances'];
  tab: string;
  setTab: (v: string) => void;
};

const PageContext = createContext<TState>({
  jobId: '',
  isLoading: false,
  error: null,
  utterances: [],
  tab: 'words',
  setTab: () => {},
});

type TProps = {
  children: ReactNode;
};

export const PageProvider = ({ children }: TProps) => {
  const { call } = useAPI();
  const { id: jobId = '' } = useParams<{ id: string }>();
  const [tab, setTab] = useState('words');

  const utterancesQuery = useQuery({
    queryKey: adminJobsKeys.utterancesByJob(jobId, ASSEMBLY_TRANSCRIPT),
    queryFn: createFetchJobUtterancesQueryFn(call),
    enabled: jobId !== '',
  });

  return (
    <PageContext.Provider
      value={{
        jobId,
        isLoading: utterancesQuery.isFetching,
        error: utterancesQuery.isError ? 'Unable to load the utterances for this job.' : null,
        utterances: utterancesQuery.data?.utterances ?? EMPTY_UTTERANCES,
        tab,
        setTab,
      }}
    >
      {children}
    </PageContext.Provider>
  );
};

export const usePageContext = () => useContext(PageContext);
